import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { getWellConfig } from "../../utils/wellsConfig";
import { PRC_WELL } from "../../utils/PRC_WELL";
import axios from "../../utils/axiosInstance";
import { ProgressDialog } from "./progressPopup";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export const OverwatchTab = ({
  overwatchSettings,
  optimizationSettings,
  setLoading,
  selectedTab,
  selectedDevice,
  refreshData,
}) => {
  const [overwatchsettings, setOverwatchSettings] = useState(overwatchSettings);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");

  const overwatchValue = useMemo(() => {
    const overwatch = optimizationSettings.find(
      (item) => item.label === "Overwatch"
    );
    return overwatch?.value;
  }, [optimizationSettings]);

  // Handle field (hrs/min/sec) or value updates
  const handleChange = (index, field, newValue) => {
    setOverwatchSettings((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        // Case 1: items that have hrs / min / sec
        if (item.fields) {
          return {
            ...item,
            fields: { ...item.fields, [field]: newValue },
          };
        }

        // Case 2: items that use a single "value"
        return { ...item, value: newValue };
      })
    );
  };

  const handleSubmit = async () => {
    // Step 1: Check if all fields are filled
    const isAnyFieldEmpty = overwatchsettings.some((item) => {
      if (item.fields) {
        // If fields exist, check for 'hrs', 'min', 'sec' fields
        return Object.keys(item.fields).some(
          (field) => item.fields[field] === ""
        );
      } else {
        // If fields do not exist, check if 'value' is empty
        return item.value === "";
      }
    });

    if (isAnyFieldEmpty) {
      console.error("❌ Please fill all fields.");
      toast.error("❌ Please fill all fields before submitting.");
      return;
    }
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      runDeviceUpdate();
    }, 100); // 100ms is enough to show the dialog
  };

  const runDeviceUpdate = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      const isFlowRate = String(overwatchValue) === "1";
      const MIN_PID_REGISTER = isFlowRate
        ? wellConfig.MIN_FLOW_PID
        : wellConfig.MIN_CASING_PRS;
      const MAX_PID_REGISTER = isFlowRate
        ? wellConfig.MAX_FLOW_PID
        : wellConfig.MAX_CASING_PRS;
      let registerList = [
        wellConfig.OVER_RUN_SETPOINT?.[0],
        wellConfig.OVER_RUN_SETPOINT?.[1],
        wellConfig.OVER_RUN_SETPOINT?.[2],
        wellConfig.OVER_RUN_TIMER?.[0],
        wellConfig.OVER_RUN_TIMER?.[1],
        wellConfig.OVER_RUN_TIMER?.[2],
        wellConfig.UNDER_RUN_SETPOINT?.[0],
        wellConfig.UNDER_RUN_SETPOINT?.[1],
        wellConfig.UNDER_RUN_SETPOINT?.[2],
        wellConfig.UNDER_RUN_TIMER?.[0],
        wellConfig.UNDER_RUN_TIMER?.[1],
        wellConfig.UNDER_RUN_TIMER?.[2],
        wellConfig.MAX_UNDER_RUN,
        wellConfig.UNDER_RUN_DEC,
        wellConfig.MAX_OVER_RUN,
        wellConfig.OVER_RUN_INC,
        MIN_PID_REGISTER,
        MAX_PID_REGISTER,
        wellConfig.UNDER_RUN,
        wellConfig.OVER_RUN,
        wellConfig.INC_DEC,
      ];
      console.log("register list in body", registerList);
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const overRunSetpoint = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Over Run Setpoint"
      );
      const overRunSetpointHourValue = overRunSetpoint?.fields?.hrs;
      const overRunSetpointMinValue = overRunSetpoint?.fields?.min;
      const overRunSetpointSecValue = overRunSetpoint?.fields?.sec;

      const overRunTimer = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Over Run Timer"
      );
      const overRunTimerHourValue = overRunTimer?.fields?.hrs;
      const overRunTimerMinValue = overRunTimer?.fields?.min;
      const overRunTimerSecValue = overRunTimer?.fields?.sec;

      const underRunSetpoint = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Under Run Setpoint"
      );
      const underRunSetpointHourValue = underRunSetpoint?.fields?.hrs;
      const underRunSetpointMinValue = underRunSetpoint?.fields?.min;
      const underRunSetpointSecValue = underRunSetpoint?.fields?.sec;

      const underRunTimer = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Under Run Timer"
      );
      const underRunTimerHourValue = underRunTimer?.fields?.hrs;
      const underRunTimerMinValue = underRunTimer?.fields?.min;
      const underRunTimerSecValue = underRunTimer?.fields?.sec;

      const maxUnderRun = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Max Under Runs"
      );
      const maxUnderRunHourValue = maxUnderRun?.value;

      const underRunDecrement = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Under Run Decrement"
      );
      const underRunDecrementHourValue =
        parseFloat(underRunDecrement?.value) * 10;

      const maxOverRuns = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Max Over Runs"
      );
      const maxOverRunsHourValue = maxOverRuns?.value;

      const overRunIncrement = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Over Run Increment"
      );
      const overRunIncrementHourValue =
        parseFloat(overRunIncrement?.value) * 10;

      const minValue = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "UnknownMin"
      );
      const minValueScaled = parseFloat(minValue?.value) * 10;

      const maxValue = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "UnknownMax"
      );
      const maxValueScaled = parseFloat(maxValue?.value) * 10;

      const underRuns = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Under Runs"
      );
      const underRunsValue = underRuns?.value;

      const overRuns = PRC_WELL.getValueFromWell(
        overwatchsettings,
        "Over Runs"
      );
      const overRunsValue = overRuns?.value;

      const incDec = PRC_WELL.getValueFromWell(overwatchsettings, "Inc / Dec");
      const incDecValue = incDec?.value;

      cmd +=
        "," +
        (wellConfig.OVER_RUN_SETPOINT?.[0] - 40001) +
        "," +
        overRunSetpointHourValue +
        "," +
        (wellConfig.OVER_RUN_SETPOINT?.[1] - 40001) +
        "," +
        overRunSetpointMinValue +
        "," +
        (wellConfig.OVER_RUN_SETPOINT?.[2] - 40001) +
        "," +
        overRunSetpointSecValue;

      cmd +=
        "," +
        (wellConfig.OVER_RUN_TIMER?.[0] - 40001) +
        "," +
        overRunTimerHourValue +
        "," +
        (wellConfig.OVER_RUN_TIMER?.[1] - 40001) +
        "," +
        overRunTimerMinValue +
        "," +
        (wellConfig.OVER_RUN_TIMER?.[2] - 40001) +
        "," +
        overRunTimerSecValue;

      cmd +=
        "," +
        (wellConfig.UNDER_RUN_SETPOINT?.[0] - 40001) +
        "," +
        underRunSetpointHourValue +
        "," +
        (wellConfig.UNDER_RUN_SETPOINT?.[1] - 40001) +
        "," +
        underRunSetpointMinValue +
        "," +
        (wellConfig.UNDER_RUN_SETPOINT?.[2] - 40001) +
        "," +
        underRunSetpointSecValue;

      cmd +=
        "," +
        (wellConfig.UNDER_RUN_TIMER?.[0] - 40001) +
        "," +
        underRunTimerHourValue +
        "," +
        (wellConfig.UNDER_RUN_TIMER?.[1] - 40001) +
        "," +
        underRunTimerMinValue +
        "," +
        (wellConfig.UNDER_RUN_TIMER?.[2] - 40001) +
        "," +
        underRunTimerSecValue;

      cmd +=
        "," + (wellConfig.MAX_UNDER_RUN - 40001) + "," + maxUnderRunHourValue;

      cmd +=
        "," +
        (wellConfig.UNDER_RUN_DEC - 40001) +
        "," +
        underRunDecrementHourValue;

      cmd +=
        "," + (wellConfig.MAX_OVER_RUN - 40001) + "," + maxOverRunsHourValue;

      cmd +=
        "," +
        (wellConfig.OVER_RUN_INC - 40001) +
        "," +
        overRunIncrementHourValue;

      cmd += "," + (MIN_PID_REGISTER - 40001) + "," + minValueScaled;

      cmd += "," + (MAX_PID_REGISTER - 40001) + "," + maxValueScaled;

      cmd += "," + (wellConfig.UNDER_RUN - 40001) + "," + underRunsValue;

      cmd += "," + (wellConfig.OVER_RUN - 40001) + "," + overRunsValue;

      cmd += "," + (wellConfig.INC_DEC - 40001) + "," + incDecValue;

      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},
            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setOverwatchSettings(overwatchSettings);
  }, [overwatchSettings]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#1e293b",
          px: 1,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ color: "#ffffffff", fontSize: 14 }}>
          Overwatch Setpoints
        </Typography>
        {overwatchValue !== Number("0") && (
          <Button
            color="primary"
            variant="contained"
            size="small"
            sx={{ fontSize: 12 }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        )}
      </Box>

      {/* Main content for items with fields */}
      {overwatchValue === Number("0") ? (
        <Box
          sx={{
            backgroundColor: "#374151",
            color: "#fff",
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 14 }}>
            Overwatch Optimization is Disabled
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "#374151",
            color: "#fff",
            py: 3,
            px: 1,
            width: "100%",
          }}
        >
          {/* Over Run Setpoint */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              Over Run Setpoint
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {["hrs", "min", "sec"].map((field) => (
                <Grid
                  key={field}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <TextField
                    label={field.toUpperCase()}
                    fullWidth
                    size="small"
                    value={overwatchsettings[0].fields[field]}
                    onChange={(e) => handleChange(0, field, e.target.value)}
                    sx={{ width: "200px" }}
                    InputLabelProps={{
                      style: { color: "#fff", fontSize: 14 },
                    }}
                    InputProps={{
                      sx: {
                        color: "#fff",
                        backgroundColor: "#1f2937",
                        padding: 0.2,
                        fontSize: 12,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Over Run Timer */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              Over Run Timer
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {["hrs", "min", "sec"].map((field) => (
                <Grid
                  key={field}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <TextField
                    label={field.toUpperCase()}
                    fullWidth
                    size="small"
                    value={overwatchsettings[1].fields[field]}
                    onChange={(e) => handleChange(1, field, e.target.value)}
                    sx={{ width: "200px" }}
                    InputLabelProps={{
                      style: { color: "#fff", fontSize: 14 },
                    }}
                    InputProps={{
                      sx: {
                        color: "#fff",
                        backgroundColor: "#1f2937",
                        padding: 0.2,
                        fontSize: 12,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Under Run Setpoint */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              Under Run Setpoint
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {["hrs", "min", "sec"].map((field) => (
                <Grid
                  key={field}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <TextField
                    label={field.toUpperCase()}
                    fullWidth
                    size="small"
                    value={overwatchsettings[2].fields[field]}
                    onChange={(e) => handleChange(2, field, e.target.value)}
                    sx={{ width: "200px" }}
                    InputLabelProps={{
                      style: { color: "#fff", fontSize: 14 },
                    }}
                    InputProps={{
                      sx: {
                        color: "#fff",
                        backgroundColor: "#1f2937",
                        padding: 0.2,
                        fontSize: 12,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Under Run Timer */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              Under Run Timer
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {["hrs", "min", "sec"].map((field) => (
                <Grid
                  item
                  key={field}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <TextField
                    label={field.toUpperCase()}
                    fullWidth
                    size="small"
                    value={overwatchsettings[3].fields[field]}
                    onChange={(e) => handleChange(3, field, e.target.value)}
                    sx={{ width: "200px" }}
                    InputLabelProps={{
                      style: { color: "#fff", fontSize: 14 },
                    }}
                    InputProps={{
                      sx: {
                        color: "#fff",
                        backgroundColor: "#1f2937",
                        padding: 0.2,
                        fontSize: 12,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Max Under Runs & Under Runs*/}
          <Grid
            sx={{
              display: "flex",
              alignItems: "stretch",
              justifyContent: "flex-start",
              gap: 4,
              borderBottom: "1px solid #a0a0a0ff",
              "@media (max-width: 1600px)": {
                flexDirection: "column",
                gap: 0,
              },
            }}
          >
            {/* Max Under Runs */}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  width: "500px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                  "@media (max-width: 1600px)": {
                    width: "200px",
                  },
                }}
              >
                Max Under Runs
              </Typography>

              <TextField
                size="small"
                value={overwatchsettings[4].value}
                onChange={(e) => handleChange(4, "value", e.target.value)}
                sx={{ width: "200px" }}
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                    padding: 0.2,
                  },
                }}
              />
            </Grid>

            {/* Under Runs */}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                py: 1.5,
                px: 2,
                "@media (max-width: 1600px)": {
                  px: 0,
                  justifyContent: "flex-start",
                },
              }}
            >
              <Typography
                sx={{
                  width: "200px",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Under Runs
              </Typography>

              <TextField
                size="small"
                value={overwatchsettings[5].value}
                onChange={(e) => handleChange(5, "value", e.target.value)}
                sx={{ width: "200px" }}
                disabled
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                    padding: 0.2,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Under Run Decrement */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              Under Run Decrement
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <TextField
                label={overwatchValue === Number("1") ? "MCF" : "PSI"}
                size="small"
                value={overwatchsettings[6].value}
                onChange={(e) => handleChange(6, "value", e.target.value)}
                sx={{ width: "200px" }}
                InputLabelProps={{
                  style: { color: "#fff", fontSize: 14 },
                }}
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    padding: 0.2,
                    fontSize: 12,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Max Over Runs & Over Runs */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "stretch",
              justifyContent: "flex-start",
              gap: 4,
              borderBottom: "1px solid #a0a0a0ff",
              "@media (max-width: 1600px)": {
                flexDirection: "column",
                gap: 0,
              },
            }}
          >
            {/* Max Over Runs */}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  width: "500px",
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                  "@media (max-width: 1600px)": {
                    width: "200px",
                  },
                }}
              >
                Max Over Runs
              </Typography>
              <TextField
                size="small"
                value={overwatchsettings[7].value}
                onChange={(e) => handleChange(7, "value", e.target.value)}
                sx={{ width: "200px" }}
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                    padding: 0.2,
                  },
                }}
              />
            </Grid>

            {/* Over Runs */}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                py: 1.5,
                px: 2,
                "@media (max-width: 1600px)": {
                  px: 0,
                  justifyContent: "flex-start",
                },
              }}
            >
              <Typography
                sx={{
                  width: "200px",
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Over Runs
              </Typography>
              <TextField
                size="small"
                value={overwatchsettings[8].value}
                onChange={(e) => handleChange(8, "value", e.target.value)}
                sx={{ width: "200px" }}
                disabled
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    fontSize: 13,
                    padding: 0.2,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Over Run Increment & Inc / Dec */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "stretch",
              justifyContent: "flex-start",
              gap: 4,
              borderBottom: "1px solid #a0a0a0ff",
              "@media (max-width: 1600px)": {
                flexDirection: "column",
                gap: 0,
              },
            }}
          >
            {/* Over Run Increment */}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  width: "500px",
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                  "@media (max-width: 1600px)": {
                    width: "200px",
                  },
                }}
              >
                Over Run Increment
              </Typography>
              <TextField
                label={overwatchValue === Number("1") ? "MCF" : "PSI"}
                size="small"
                value={overwatchsettings[9].value}
                onChange={(e) => handleChange(9, "value", e.target.value)}
                sx={{ width: "200px" }}
                InputLabelProps={{
                  style: { color: "#fff", fontSize: 14 },
                }}
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    padding: 0.2,
                    fontSize: 13,
                  },
                }}
              />
            </Grid>
            {/* Inc / Dec */}
            <Grid
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                py: 1.5,
                px: 2,
                "@media (max-width: 1600px)": {
                  px: 0,
                  justifyContent: "flex-start",
                },
              }}
            >
              <Typography
                sx={{
                  width: "200px",
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Inc / Dec
              </Typography>
              <TextField
                size="small"
                value={overwatchsettings[10].value}
                onChange={(e) => handleChange(10, "value", e.target.value)}
                sx={{ width: "200px" }}
                disabled
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    padding: 0.2,
                    fontSize: 13,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* UnknownMin */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              {overwatchValue === Number("1")
                ? "Min Flow Rate PID Auto SP"
                : "Min Casing PSI PID Auto SP"}
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <TextField
                label={overwatchValue === Number("1") ? "MCF" : "PSI"}
                size="small"
                value={overwatchsettings[11].value}
                onChange={(e) => handleChange(11, "value", e.target.value)}
                sx={{ width: "200px" }}
                InputLabelProps={{
                  style: { color: "#fff", fontSize: 14 },
                }}
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    padding: 0.2,
                    fontSize: 12,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* UnknownMax */}
          <Grid
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #a0a0a0ff",
              py: 1.5,
            }}
          >
            <Typography
              sx={{
                width: "500px",
                color: "#ffffffff",
                fontWeight: 600,
                fontSize: 13,
                "@media (max-width: 1600px)": {
                  width: "200px",
                },
              }}
            >
              {overwatchValue === Number("1")
                ? "Max Flow Rate PID Auto SP"
                : "Max Casing PSI PID Auto SP"}
            </Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <TextField
                label={overwatchValue === Number("1") ? "MCF" : "PSI"}
                size="small"
                value={overwatchsettings[12].value}
                onChange={(e) => handleChange(12, "value", e.target.value)}
                sx={{ width: "200px" }}
                InputLabelProps={{
                  style: { color: "#fff", fontSize: 14 },
                }}
                InputProps={{
                  sx: {
                    color: "#fff",
                    backgroundColor: "#1f2937",
                    padding: 0.2,
                    fontSize: 12,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Box>
  );
};
